import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Music, Plus, Trash2, Download } from "lucide-react";

const Input = (p) => <input {...p} className={`w-full rounded-sm border border-white/15 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-gray-600 focus:border-[#1f63c9] focus:outline-none ${p.className || ""}`} />;

export function MusicCard() {
  const [m, setM] = useState(null);
  const [draft, setDraft] = useState({ title: "", artist: "", url: "" });
  const [volume, setVolume] = useState(35);
  useEffect(() => { api.get("/music/config").then((r) => { setM(r.data); setVolume(r.data.volume); }).catch(() => {}); }, []);

  const save = async (body, msg) => {
    try { const { data } = await api.put("/music/config", body); setM(data); if (msg) toast.success(msg); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const starter = async () => {
    const { data } = await api.post("/music/starter-pack"); setM(data);
    toast.success(`Loaded ${data.tracks.length} tracks — the stream picks them up within 30 minutes, or restart it now`);
  };
  const addTrack = async () => {
    if (!draft.title.trim() || !/^https?:\/\//.test(draft.url.trim())) return toast.error("Need a title and a direct MP3 link (https://…)");
    await save({ tracks: [...m.tracks, { ...draft, title: draft.title.trim(), url: draft.url.trim(), license: "user-provided" }] }, "Track added");
    setDraft({ title: "", artist: "", url: "" });
  };

  if (!m) return null;
  return (
    <div className="glass rounded-sm border border-[#ffd60a]/40 p-6" data-testid="music-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Music className="h-5 w-5 text-[#ffd60a]" />
          <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">Stream Music · Smooth Jazz Bed</div>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-200">
          <input type="checkbox" data-testid="music-enabled" checked={m.enabled} onChange={(e) => save({ enabled: e.target.checked }, e.target.checked ? "Music on" : "Music off")} className="h-4 w-4 accent-[#1f63c9]" />
          {m.enabled ? "On" : "Off"}
        </label>
      </div>
      <p className="mt-1 text-sm text-gray-300">
        Plays under the graphics on the 24/7 stream only (not on the website), shuffled on a loop, and fades to silence whenever a warning Cut-In is on air.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[220px]">
          <div className="mb-1 flex justify-between font-oswald text-[11px] uppercase tracking-widest text-gray-400"><span>Volume</span><span data-testid="music-volume-label">{volume}%</span></div>
          <input type="range" min={0} max={100} value={volume} data-testid="music-volume" onChange={(e) => setVolume(Number(e.target.value))} onMouseUp={() => save({ volume }, "Volume saved")} onTouchEnd={() => save({ volume }, "Volume saved")} className="w-full accent-[#1f63c9]" />
        </div>
        <button type="button" data-testid="music-starter-pack" onClick={starter} className="inline-flex items-center gap-2 rounded-sm bg-[#1f63c9] px-4 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-white hover:bg-[#2a74d8]">
          <Download className="h-4 w-4" /> Load starter pack (15 tracks)
        </button>
      </div>

      <div className="mt-4 rounded-sm border border-white/10 bg-black/30" data-testid="music-track-list">
        {m.tracks.length === 0 && <div className="p-4 text-sm text-gray-400" data-testid="music-empty">No tracks yet — load the starter pack or add your own MP3 links below.</div>}
        {m.tracks.map((t, i) => (
          <div key={t.url} className="flex items-center gap-3 border-b border-white/5 px-4 py-2 last:border-0" data-testid={`music-track-${i}`}>
            <span className="w-6 font-mono text-[11px] text-gray-500">{i + 1}</span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-white">{t.title}</div>
              <div className="truncate text-[11px] text-gray-500">{t.artist}{t.artist ? " · " : ""}{t.license}</div>
            </div>
            <button type="button" data-testid={`music-remove-${i}`} onClick={() => save({ tracks: m.tracks.filter((x) => x.url !== t.url) }, "Removed")} className="text-gray-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>

      <div className="mt-4 grid gap-2 md:grid-cols-[1fr_1fr_2fr_auto]">
        <Input data-testid="music-add-title" placeholder="Track title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
        <Input data-testid="music-add-artist" placeholder="Artist" value={draft.artist} onChange={(e) => setDraft({ ...draft, artist: e.target.value })} />
        <Input data-testid="music-add-url" placeholder="https://…/track.mp3 (direct link)" value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} />
        <button type="button" data-testid="music-add-btn" onClick={addTrack} className="inline-flex items-center justify-center gap-1 rounded-sm border border-white/20 px-3 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-white hover:border-[#1f63c9]"><Plus className="h-4 w-4" /> Add</button>
      </div>

      <div className="mt-4 rounded-sm border border-amber-400/30 bg-amber-500/5 p-3 text-xs text-amber-100/90" data-testid="music-attribution">
        <b>Credit required for the starter pack</b> — paste this in your YouTube stream description: <span className="font-mono text-amber-200">{m.attribution}</span>
        <div className="mt-1 text-amber-200/70">Only use music you have rights to. The real 90s Weather Channel tracks are copyrighted and will get a 24/7 stream muted or struck by YouTube.</div>
      </div>
    </div>
  );
}
